<template>
  <div class="community-shell">
    <div v-if="!shellCommunity" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <template v-else>
      <div class="shell-cover" :style="coverStyle" />

      <div class="shell-banner">
        <div class="shell-banner-inner">
          <div class="banner-row d-flex align-items-center">
            <h4 class="shell-name mb-0">{{ shellCommunity.name }}</h4>
            <button
              v-if="shellCommunity.description"
              class="btn-toggle-description ml-3"
              @click="showDescription = !showDescription"
            >
              {{ showDescription ? "Ocultar descrição" : "Mostrar descrição" }}
              <i
                :class="
                  showDescription
                    ? 'fa-light fa-angles-up'
                    : 'fa-light fa-angles-down'
                "
              />
            </button>
            <community-bell class="ml-auto" :community-id="communityId" />
          </div>
          <p
            v-if="shellCommunity.description && showDescription"
            class="shell-description mb-0"
          >
            {{ shellCommunity.description }}
          </p>
        </div>
      </div>

      <div class="shell-container">
        <div class="shell-body">
          <aside class="shell-nav">
            <div class="nav-card">
              <label class="nav-search mb-0">
                <i class="fa-light fa-magnifying-glass" />
                <input
                  :value="search"
                  type="text"
                  placeholder="Buscar"
                  @input="$emit('search', $event.target.value)"
                />
              </label>
              <nav class="nav-items">
                <router-link
                  class="nav-item"
                  :class="{ active: active === 'feed' }"
                  :to="{ name: 'community', params: { id: communityId } }"
                >
                  <span class="nav-icon"
                    ><i class="fa-light fa-align-justify"
                  /></span>
                  Feed
                </router-link>
                <router-link
                  class="nav-item"
                  :class="{ active: active === 'forum' }"
                  :to="{
                    name: 'community-forums',
                    params: { id: communityId }
                  }"
                >
                  <span class="nav-icon"
                    ><i class="fa-light fa-comments"
                  /></span>
                  Fórum
                </router-link>
              </nav>
            </div>
          </aside>

          <div class="shell-main">
            <slot />
          </div>

          <aside class="shell-aside">
            <slot name="aside">
              <trending-posts :posts="trending" @select="onTrendingSelect" />
            </slot>
          </aside>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";
import CommunityBell from "./CommunityBell.vue";
import TrendingPosts from "./TrendingPosts.vue";

export default {
  name: "CommunityShell",
  components: { CommunityBell, TrendingPosts },
  props: {
    communityId: {
      type: String,
      required: true
    },
    active: {
      type: String,
      default: "feed"
    },
    search: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      showDescription: true
    };
  },
  computed: {
    ...mapState("community", [
      "community",
      "trendingCommunityId",
      "trendingPosts"
    ]),
    shellCommunity() {
      return this.community && this.community.id === this.communityId
        ? this.community
        : null;
    },
    trending() {
      return this.trendingCommunityId === this.communityId
        ? this.trendingPosts
        : [];
    },
    coverStyle() {
      const cover = this.shellCommunity && this.shellCommunity.cover;
      return {
        backgroundImage: cover ? `url(${cover})` : ""
      };
    }
  },
  created() {
    if (!this.shellCommunity) {
      this.$store
        .dispatch("community/get", this.communityId)
        .catch(() =>
          this.$message.error("Não foi possível carregar a comunidade")
        );
    }
    this.$store
      .dispatch("community/getTrending", this.communityId)
      .catch(() => {});
  },
  methods: {
    onTrendingSelect(post) {
      if (this.$route.name === "community") {
        const element = document.getElementById(`community-post-${post.id}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        return;
      }
      this.$router.push({
        name: "community",
        params: { id: this.communityId }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.community-shell {
  padding-top: 72px;
  padding-bottom: 40px;
  font-family: "Inter", "Noto Sans", sans-serif;

  .shell-cover {
    height: 258px;
    background-color: rgba(128, 128, 128, 0.12);
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
  }

  .shell-banner {
    background: rgba(128, 128, 128, 0.06);
    border-radius: 0 0 8px 8px;
    padding: 24px 16px;
    margin-bottom: 16px;

    .shell-banner-inner {
      max-width: 1112px;
      margin: 0 auto;
    }

    .shell-name {
      font-size: 20px;
      font-weight: 400;
      line-height: 24px;
    }

    .btn-toggle-description {
      border: none;
      background: transparent;
      padding: 4px 8px;
      color: inherit;
      font-size: 14px;
      line-height: 20px;
      display: inline-flex;
      align-items: center;
      gap: 4px;

      i {
        font-size: 12px;
      }
    }

    .shell-description {
      font-size: 14px;
      font-weight: 300;
      line-height: 20px;
      opacity: 0.85;
      margin-top: 16px;
      max-width: 807px;
      overflow-wrap: anywhere;
    }
  }

  .shell-container {
    max-width: calc(1112px + 32px);
    margin: 0 auto;
    padding: 0 16px;
  }

  .shell-body {
    display: grid;
    grid-template-columns: 243px minmax(0, 1fr) 240px;
    gap: 16px;
    align-items: start;
  }

  .shell-nav {
    .nav-card {
      padding: 16px;
      border-radius: 4px;
      border: 1px solid rgba(128, 128, 128, 0.2);
      background: rgba(128, 128, 128, 0.06);
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .nav-search {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 40px;
      padding: 0 8px;
      border-radius: 4px;
      border: 1px solid rgba(128, 128, 128, 0.25);
      background: rgba(128, 128, 128, 0.08);
      cursor: text;

      i {
        font-size: 14px;
        opacity: 0.7;
      }

      input {
        border: none;
        background: transparent;
        color: inherit;
        font-size: 14px;
        line-height: 20px;
        width: 100%;
        outline: none;

        &::placeholder {
          color: inherit;
          opacity: 0.55;
        }
      }
    }

    .nav-items {
      display: flex;
      flex-direction: column;
      padding-bottom: 8px;
    }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 6px 0;
      padding: 6px 12px;
      border-radius: 2px;
      color: inherit;
      text-decoration: none;
      font-size: 14px;
      line-height: 20px;
      opacity: 0.75;

      .nav-icon {
        width: 28px;
        display: inline-flex;
        justify-content: flex-end;

        i {
          font-size: 16px;
        }
      }

      &:hover {
        opacity: 1;
        background: rgba(128, 128, 128, 0.1);
      }

      &.active {
        opacity: 1;
        background: rgba(128, 128, 128, 0.16);
      }
    }
  }

  .theme-dark & {
    .shell-banner {
      background: #0d161c;
    }

    .shell-banner .shell-name {
      color: #fcfcfc;
    }

    .shell-banner .btn-toggle-description {
      color: #438a98;
    }

    .shell-banner .shell-description {
      color: #7a8c99;
      opacity: 1;
    }

    .shell-nav .nav-card {
      background: #0d161c;
      border-color: #07151f;
    }

    .shell-nav .nav-search {
      background: #0c1013;
      border-color: #182126;

      i,
      input::placeholder {
        color: #7a8c99;
        opacity: 1;
      }
    }

    .shell-nav .nav-item {
      color: rgba(252, 252, 252, 0.65);
      opacity: 1;

      &:hover {
        background: rgba(15, 41, 56, 0.5);
      }

      &.active {
        background: #0f2938;
      }
    }
  }

  @media (max-width: 1199px) {
    .shell-body {
      grid-template-columns: 243px minmax(0, 1fr);
    }

    .shell-aside {
      display: none;
    }
  }

  @media (max-width: 767px) {
    .shell-cover {
      height: 140px;
    }

    .shell-body {
      grid-template-columns: minmax(0, 1fr);
    }

    .shell-nav {
      .nav-items {
        flex-direction: row;
        padding-bottom: 0;
      }

      .nav-item {
        flex: 1;
        justify-content: center;

        .nav-icon {
          width: auto;
        }
      }
    }
  }
}
</style>
